import {useState} from 'react'
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {Listbox, ListboxButton, ListboxOption, ListboxOptions} from '@headlessui/react'
import {infoIcon, couponIcon} from '@img/index.js'
import {BOOKING_CONSTRAINTS} from '@/constants/booking'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import ModalFooter from '@/customComponents/uiComponents/modal/ModalFooter.jsx'
import FlatRateBox from '@/customComponents/editRideComponents/FlatRateBox.jsx'
import CouponForm from '@/customComponents/editRideComponents/PageModals/CouponForm.jsx'
import AirportPickupFeeInfo from '@/customComponents/editRideComponents/PageModals/AirportPickupFeeInfo.jsx'

function formatBreakdownValue(val, isLast) {
    if (isLast) return val < 0 ? `-$${Math.ceil(Math.abs(val / 100))}` : `$${Math.ceil(val / 100)}`
    return val < 0 ? `-$${Math.abs(val / 100).toFixed(2)}` : `$${(val / 100).toFixed(2)}`
}

function PricingBreakdown({items, driverTip, setDriverTip, onTipChange}) {
    // Read at render time — DRIVER_TIP_OPTIONS is a config getter, [] until /config/app loads.
    const tipOptions = BOOKING_CONSTRAINTS.DRIVER_TIP_OPTIONS
    const [isTipRecalculating, setIsTipRecalculating] = useState(false)
    const [airportFeePopup, setAirportFeePopup] = useState(false)

    const filtered = items.filter(item => Number(item.value) !== 0)

    return (
        <div className="hopn-view-in">
            <div className="flex flex-col gap-0">
                {filtered.map((item, index) => {
                    const isLast = index === filtered.length - 1
                    return (
                        <div key={item.key || index} className="flex justify-between items-center border-b border-[#cfcdcd] pb-3.5 mb-3.5">
                            <span className="flex items-center gap-1">
                                {item.label}
                                {item.key === 'gratuity' && (
                                    isTipRecalculating ? (
                                        <Loader color="#201e20" height={8} width={1}/>
                                    ) : (
                                        <Listbox value={driverTip} onChange={async (val) => {
                                            setDriverTip(val)
                                            setIsTipRecalculating(true)
                                            if (onTipChange) await onTipChange(val)
                                            setIsTipRecalculating(false)
                                        }} disabled={isTipRecalculating}>
                                            <ListboxButton className="text-sm border border-(--border-color) rounded-full px-3 py-1 ml-1 outline-none cursor-pointer flex items-center gap-1 data-disabled:opacity-50">
                                                {BOOKING_CONSTRAINTS.tipLabel(driverTip)}
                                                <svg width="10" height="6" viewBox="0 0 12 8" fill="none"><path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                            </ListboxButton>
                                            <ListboxOptions anchor="bottom" className="mt-1 rounded-xl bg-white shadow-lg overflow-hidden z-99999 outline-none">
                                                {tipOptions.map(v => (
                                                    <ListboxOption key={v} value={String(v)} className="px-4 py-2 text-sm cursor-pointer data-focus:bg-(--bg-grey) data-selected:font-bold">
                                                        {BOOKING_CONSTRAINTS.tipLabel(v)}
                                                    </ListboxOption>
                                                ))}
                                            </ListboxOptions>
                                        </Listbox>
                                    )
                                )}
                                {item.key === 'airport_pickup_fee' && (
                                    <button type="button" onClick={() => setAirportFeePopup(true)}
                                            className="cursor-pointer bg-transparent border-0 p-0">
                                        <img src={infoIcon} alt="Info"/>
                                    </button>
                                )}
                            </span>
                            <span className="font-semibold text-lg">
                                {formatBreakdownValue(item.value, isLast)}
                            </span>
                        </div>
                    )
                })}
            </div>
            <FlatRateBox/>

            <Modal isOpen={airportFeePopup} onClose={() => setAirportFeePopup(false)}>
                <ModalHeader title="Airport Pickup Fee" onClose={() => setAirportFeePopup(false)}/>
                <AirportPickupFeeInfo/>
            </Modal>
        </div>
    )
}

export default function CarSection({
    availableCars = [], selectedCar, committedCarTypeId, setSelectedCar,
    onDone, onClose, isLoading, isRecalculating, error,
    driverTip, setDriverTip, onTipChange, onCouponRecalculate, defaultCouponCode,
    breakdownCarId, setBreakdownCarId,
    couponPolicy = {canEdit: true, blockedReason: null},
}) {
    const [showCoupon, setShowCoupon] = useState(false)

    const getCarId = (car) => car.vehicle_type_id ?? car.id
    const isSelected = (car) => selectedCar != null && String(getCarId(selectedCar)) === String(getCarId(car))
    const hasChanges = selectedCar != null && String(getCarId(selectedCar)) !== String(committedCarTypeId)

    // Breakdown view for a specific car
    if (breakdownCarId) {
        const breakdownCar = availableCars.find(c => String(getCarId(c)) === String(breakdownCarId))
        if (breakdownCar?.pricing_breakdown) {
            return (
                <PricingBreakdown
                    items={breakdownCar.pricing_breakdown}
                    driverTip={driverTip} setDriverTip={setDriverTip} onTipChange={onTipChange}
                />
            )
        }
    }

    // Vehicle selection view
    return (
        <div className="hopn-view-in">
            <p className="text-lg mb-6">“All ride options are powered by verified, insured professionals trained to limousine standards.”</p>

            {isRecalculating ? (
                <div className="py-8 text-center"><Loader/></div>
            ) : availableCars.length === 0 ? (
                <div className="py-8 text-center text-(--text-secondary)">No vehicles available for this trip.</div>
            ) : (
                <div className="flex flex-col gap-2.5 mb-6">
                    {availableCars.map(car => {
                        const carId = getCarId(car)
                        const selected = isSelected(car)
                        const hasDiscount = car.pricing?.online_discount > 0
                        return (
                            <label key={carId}
                                   className={`flex justify-between items-center rounded-3xl cursor-pointer transition-all ${selected ? 'bg-(--text-primary) text-white' : 'bg-(--bg-grey)'}`}>
                                <input type="radio" name="car" value={carId} checked={selected}
                                       onChange={() => setSelectedCar(car)} className="hidden"/>

                                {/* Left — Price. /confirm quotes on load now, so there is no
                                    price-less state to stand in for. */}
                                <div className="flex pl-5 items-center max-md:pl-2.5 min-w-0">
                                    <div className="flex flex-col gap-1 mr-4 max-md:mr-1.5">
                                        {car.total_price != null && (
                                            <span className="text-[55px] max-md:text-[28px] max-md:leading-none">${Math.ceil(car.total_price / 100)}</span>
                                        )}
                                        <span className={`text-md max-md:text-[10px] ${selected ? 'text-white' : 'text-(--text-primary)'}`}>Tip included</span>
                                    </div>
                                    {hasDiscount && (
                                        <div className="mt-1 relative text-md max-md:text-[10px] max-md:leading-tight">
                                            <span className="line-through text-xl max-md:text-md max-md:pr-4">
                                                ${Math.ceil((car.pricing.subtotal + car.pricing.tax) / 100)}
                                            </span>
                                            {car.pricing_breakdown && (
                                                <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setBreakdownCarId(carId) }}
                                                        className="absolute right-0 top-0 cursor-pointer bg-transparent border-0 p-0 ml-1 align-middle">
                                                    <img src={infoIcon} alt="Info" className="max-md:w-3 max-md:h-3" style={{filter: selected ? 'brightness(0) invert(1)' : 'none'}}/>
                                                </button>
                                            )}
                                            <span className={`block mt-0.5 ${selected ? 'text-(--accent-green-light)' : 'text-(--text-primary)'}`}>
                                                Discount <br/> ({car.pricing.online_discount_percentage}% -${Math.ceil(car.pricing.online_discount / 100)})
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Right — Car name + image */}
                                <div className="flex flex-col items-end rounded-3xl p-3 bg-white shadow-[0_4px_32px_#00000029] max-md:shrink-0 max-md:rounded-xl max-md:p-4">
                                    <span className="text-sm w-full text-center text-(--text-primary) max-md:text-[10px] max-md:leading-tight">
                                        {car.vehicle_name && <span className="font-bold">{car.vehicle_name}</span>}
                                        {car.vehicle_reference && <span className="font-normal"> {car.vehicle_reference}</span>}
                                        {car.vehicles_needed > 1 && <span> x{car.vehicles_needed}</span>}
                                    </span>
                                    <div className="w-35 h-20 flex items-center justify-center max-md:w-20 max-md:h-12">
                                        <img src={car.vehicle_image} alt={car.vehicle_name} className="max-w-full max-h-full object-contain"/>
                                    </div>
                                </div>
                            </label>
                        )
                    })}
                </div>
            )}

            {/* Apply Coupon */}
            <div className="mb-5 relative group inline-block">
                <button type="button" disabled={!couponPolicy.canEdit} onClick={() => setShowCoupon(true)}
                        className="cursor-pointer flex items-center gap-2 bg-transparent border-0 p-0 text-xl max-md:text-base text-(--text-primary) disabled:opacity-50 disabled:cursor-not-allowed">
                    <img src={couponIcon} alt="discount" className="max-md:w-8"/>
                    <span>{selectedCar?.pricing?.discount_code_discount > 0 || defaultCouponCode ? 'Update Coupon' : 'Apply Coupon'}</span>
                </button>
                {!couponPolicy.canEdit && couponPolicy.blockedReason && (
                    <div className="hidden group-hover:block absolute top-full left-0 mt-2 bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-10 w-max max-w-[280px]">
                        <p className="mb-0!">{couponPolicy.blockedReason}</p>
                    </div>
                )}
            </div>

            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

            <ModalFooter>
                <button type="button" onClick={onDone}
                        disabled={!selectedCar || !hasChanges || isLoading}
                        className="w-full py-4 rounded-full bg-(--text-primary) text-white text-base border-0 cursor-pointer disabled:opacity-50 flex items-center justify-center">
                    {isLoading ? <Loader color="#fff"/> : 'Done'}
                </button>
            </ModalFooter>

            {/* Coupon Popup */}
            <Modal isOpen={showCoupon} onClose={() => setShowCoupon(false)}>
                <ModalHeader title="Add A Coupon" onClose={() => setShowCoupon(false)}/>
                <CouponForm
                    carDetails={selectedCar}
                    setApplyCoupon={setShowCoupon}
                    onRecalculate={onCouponRecalculate}
                    defaultCouponCode={defaultCouponCode}
                />
            </Modal>
        </div>
    )
}
